import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  ArrowLeft, Hand, Activity, Brain, Mic, Home, Heart, Eye,
  Play, Check, ChevronDown, ChevronUp, AlertTriangle,
  Star, Zap, Award, Sparkles, Clock
} from "lucide-react";
import axios from "axios";
import Navigation from "@/components/Navigation";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const programIcons = { hand: Hand, activity: Activity, brain: Brain, mic: Mic, home: Home, heart: Heart, eye: Eye };
const tierLabels = { beginner: "Beginner", intermediate: "Intermediate", expert: "Expert" };
const tierColors = {
  beginner: { bg: "bg-green-50", text: "text-green-600", border: "border-green-200", badge: "bg-green-100 text-green-700" },
  intermediate: { bg: "bg-amber-50", text: "text-amber-600", border: "border-amber-200", badge: "bg-amber-100 text-amber-700" },
  expert: { bg: "bg-red-50", text: "text-red-600", border: "border-red-200", badge: "bg-red-100 text-red-700" },
};

const NeuroThryvPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeProgram, setActiveProgram] = useState(null);
  const [activeTier, setActiveTier] = useState("beginner");
  const [expandedExercise, setExpandedExercise] = useState(null);
  const [completed, setCompleted] = useState(() => {
    const saved = localStorage.getItem("neurothryv_completed");
    return saved ? JSON.parse(saved) : {};
  });

  useEffect(() => {
    axios.get(`${API}/neurothryv/programs`).then(r => { setData(r.data); setActiveProgram(r.data.programs[0]?.id); }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const toggleComplete = (id) => {
    const updated = { ...completed, [id]: !completed[id] };
    setCompleted(updated);
    localStorage.setItem("neurothryv_completed", JSON.stringify(updated));
  };

  const currentProgram = data?.programs?.find(p => p.id === activeProgram);
  const currentExercises = currentProgram?.tiers?.[activeTier] || [];
  const totalExercises = data?.programs?.reduce((sum, p) => sum + Object.values(p.tiers).flat().length, 0) || 0;
  const completedCount = Object.values(completed).filter(Boolean).length;

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navigation />
      <section className="pt-28 pb-20 px-6 lg:px-12" data-testid="neurothryv-section">
        <div className="container mx-auto max-w-5xl">
          <Link to="/" className="inline-flex items-center gap-2 text-[#4FB3D4] hover:text-[#4FB3D4]/80 mb-6 text-sm">
            <ArrowLeft className="h-4 w-4" /> Back to Home
          </Link>

          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-[#4FB3D4]/10 text-[#4FB3D4] px-4 py-2 rounded-full text-xs font-bold mb-4">
              <Sparkles className="h-3 w-3" /> 7 Apps in One | OT & PT Guided
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#1A2A3A] mb-3" data-testid="neurothryv-title">
              NeuroThryv
            </h1>
            <p className="text-sm text-[#1A2A3A]/60 max-w-lg mx-auto">
              Stroke recovery exercises designed from Occupational Therapy and Physical Therapy expertise. 7 programs, 3 difficulty tiers, 20 guided scenarios. Incremental steps. Exponential Thryving.
            </p>
          </div>

          {/* Disclaimer */}
          <div className="bg-red-50 border-2 border-red-200 rounded-xl px-5 py-4 mb-8 flex items-start gap-3" data-testid="neurothryv-disclaimer">
            <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-red-700 font-medium mb-1">Medical Disclaimer</p>
              <p className="text-xs text-red-600">{data?.disclaimer || "This app is for educational use only. It does not replace professional OT, PT, or medical treatment. Always consult your healthcare team. Stop immediately if you experience pain or adverse symptoms."}</p>
            </div>
          </div>

          {/* Progress */}
          <div className="bg-white rounded-xl shadow-sm border border-[#E5E7EB]/50 p-4 mb-8 flex items-center gap-4" data-testid="progress-bar">
            <Award className="h-5 w-5 text-[#A69BE0]" />
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-[#1A2A3A]">Your Progress</span>
                <span className="text-xs text-[#1A2A3A]/50">{completedCount} / {totalExercises} exercises</span>
              </div>
              <div className="h-2 bg-[#E5E7EB] rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#4FB3D4] to-[#A69BE0] rounded-full transition-all" style={{ width: `${totalExercises ? (completedCount / totalExercises) * 100 : 0}%` }} />
              </div>
            </div>
          </div>

          {/* Instruction Videos */}
          {data?.instruction_videos && (
            <div className="mb-10">
              <h2 className="font-heading text-lg font-semibold text-[#1A2A3A] mb-4 flex items-center gap-2">
                <Play className="h-5 w-5 text-[#4FB3D4]" /> Getting Started — Instruction Videos
              </h2>
              <div className="grid md:grid-cols-3 gap-4">
                {data.instruction_videos.map((v, i) => (
                  <a key={i} href={`https://www.youtube.com/watch?v=${v.youtube_id}`} target="_blank" rel="noopener noreferrer" data-testid={`intro-video-${i}`}>
                    <Card className="bg-white rounded-xl overflow-hidden hover:shadow-card transition-all group">
                      <div className="relative h-28 bg-[#1A2A3A]">
                        <img src={`https://img.youtube.com/vi/${v.youtube_id}/mqdefault.jpg`} alt={v.title} className="w-full h-full object-cover opacity-70" />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center group-hover:scale-110 transition-transform">
                            <Play className="h-4 w-4 text-[#4FB3D4] ml-0.5" />
                          </div>
                        </div>
                      </div>
                      <CardContent className="p-3">
                        <h3 className="text-xs font-semibold text-[#1A2A3A] line-clamp-2">{v.title}</h3>
                        <p className="text-[10px] text-[#1A2A3A]/50 mt-0.5">{v.description}</p>
                      </CardContent>
                    </Card>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Program Selector */}
          <div className="flex gap-2 overflow-x-auto pb-2 mb-4 scrollbar-hide">
            {data?.programs?.map(p => {
              const Icon = programIcons[p.icon] || Brain;
              const isActive = activeProgram === p.id;
              return (
                <button key={p.id} onClick={() => { setActiveProgram(p.id); setExpandedExercise(null); }} className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${isActive ? "bg-[#4FB3D4] text-white shadow-md" : "bg-white text-[#1A2A3A]/60 border border-[#E5E7EB] hover:border-[#4FB3D4]/30"}`} data-testid={`program-${p.id}`}>
                  <Icon className="h-3.5 w-3.5" /> {p.name}
                </button>
              );
            })}
          </div>

          {/* Active Program */}
          {currentProgram && (
            <div>
              <div className="bg-white rounded-xl shadow-sm border border-[#E5E7EB]/50 p-5 mb-5">
                <div className="flex items-start gap-3">
                  {(() => { const Icon = programIcons[currentProgram.icon] || Brain; return <div className="w-10 h-10 rounded-lg bg-[#4FB3D4]/10 flex items-center justify-center flex-shrink-0"><Icon className="h-5 w-5 text-[#4FB3D4]" /></div>; })()}
                  <div>
                    <h2 className="font-heading text-lg font-semibold text-[#1A2A3A]" data-testid="program-title">{currentProgram.name}</h2>
                    <p className="text-xs text-[#A69BE0] font-medium">{currentProgram.discipline}</p>
                    <p className="text-sm text-[#1A2A3A]/60 mt-1">{currentProgram.description}</p>
                  </div>
                </div>
              </div>

              {/* Tier Selector */}
              <div className="flex gap-2 mb-5">
                {Object.keys(tierLabels).map(tier => {
                  const c = tierColors[tier];
                  const exerciseCount = currentProgram.tiers[tier]?.length || 0;
                  return (
                    <button key={tier} onClick={() => { setActiveTier(tier); setExpandedExercise(null); }} className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all ${activeTier === tier ? `${c.badge} ring-2 ring-offset-1 ring-${tier === "beginner" ? "green" : tier === "intermediate" ? "amber" : "red"}-300` : "bg-white text-[#1A2A3A]/50 border border-[#E5E7EB]"}`} data-testid={`tier-${tier}`}>
                      {tier === "beginner" && <Star className="h-3 w-3" />}
                      {tier === "intermediate" && <Zap className="h-3 w-3" />}
                      {tier === "expert" && <Award className="h-3 w-3" />}
                      {tierLabels[tier]} ({exerciseCount})
                    </button>
                  );
                })}
              </div>

              {/* Exercises */}
              <div className="space-y-3">
                {currentExercises.length === 0 ? (
                  <p className="text-sm text-[#1A2A3A]/40 text-center py-6">No exercises at this level yet.</p>
                ) : (
                  currentExercises.map((ex, i) => {
                    const isExpanded = expandedExercise === ex.id;
                    const isDone = completed[ex.id];
                    const c = tierColors[activeTier];
                    return (
                      <Card key={ex.id} className={`rounded-xl border overflow-hidden transition-all ${isDone ? "bg-green-50/50 border-green-200" : `${c.bg} ${c.border}`}`} data-testid={`exercise-${ex.id}`}>
                        <button className="w-full p-4 flex items-start justify-between gap-3 text-left" onClick={() => setExpandedExercise(isExpanded ? null : ex.id)}>
                          <div className="flex items-start gap-3">
                            <button onClick={(e) => { e.stopPropagation(); toggleComplete(ex.id); }} className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${isDone ? "bg-green-500 border-green-500" : "border-[#E5E7EB] hover:border-[#4FB3D4]"}`} data-testid={`check-${ex.id}`}>
                              {isDone && <Check className="h-3.5 w-3.5 text-white" />}
                            </button>
                            <div>
                              <h3 className={`font-semibold text-sm ${isDone ? "text-green-700 line-through" : "text-[#1A2A3A]"}`}>{ex.name}</h3>
                              <p className="text-xs text-[#1A2A3A]/50 mt-0.5">{ex.goal}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] text-[#1A2A3A]/40 flex items-center gap-1"><Clock className="h-3 w-3" /> {ex.reps}</span>
                            {isExpanded ? <ChevronUp className="h-4 w-4 text-[#1A2A3A]/30" /> : <ChevronDown className="h-4 w-4 text-[#1A2A3A]/30" />}
                          </div>
                        </button>
                        {isExpanded && (
                          <div className="px-4 pb-4 ml-9">
                            <div className="bg-white rounded-lg p-4 border border-[#E5E7EB]/50">
                              <p className="text-xs font-semibold text-[#1A2A3A] mb-2">Instructions:</p>
                              <p className="text-sm text-[#1A2A3A]/70 leading-relaxed">{ex.instructions}</p>
                              <div className="flex items-center gap-4 mt-3 pt-3 border-t border-[#E5E7EB]/50">
                                <span className="text-xs text-[#1A2A3A]/50"><strong>Goal:</strong> {ex.goal}</span>
                                <span className="text-xs text-[#1A2A3A]/50"><strong>Reps:</strong> {ex.reps}</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </Card>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      <footer className="py-8 px-6 bg-[#1A2A3A] text-white text-center">
        <p className="text-white/50 text-xs mb-1">NeuroThryv is for educational use only. It does not replace professional medical care.</p>
        <p className="text-white/40 text-xs">&copy; 2025 ThryvLife by Brian Bass (BriVonB). Incremental Steps. Exponential Thryving.</p>
      </footer>
    </div>
  );
};

export default NeuroThryvPage;
